import React, { useEffect, useRef, useState } from 'react';

// How far the sheet must be pulled down, or how fast, before letting go
// dismisses it rather than springing it back
const DISMISS_PX = 110;
const DISMISS_VELOCITY = 0.6; // px per ms

/**
 * A bottom sheet: the phone's answer to a dropdown menu.
 *
 * A 268 px popover anchored to a 38 px button is a desktop idea; on a phone the
 * list wants the full width, rows a thumb can hit, and a way out that does not
 * need aiming. So the sheet rises from the bottom over a dimmed backdrop,
 * scrolls inside itself, and closes on a tap outside, on Escape, or by being
 * dragged down by its handle (or anywhere, while its list is scrolled to the
 * top). The home indicator's strip stays clear through the safe-area inset.
 */
const Sheet = ({ open, title, onClose, children, className = '' }) => {
  const panel = useRef(null);
  const body = useRef(null);
  const drag = useRef(null);
  const [pull, setPull] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setPull(0);
  }, [open]);

  if (!open) return null;

  const onPointerDown = (event) => {
    // From the list, only once it is at the top: otherwise the gesture is a scroll
    const fromBody = body.current?.contains(event.target);
    if (fromBody && body.current.scrollTop > 0) return;
    // A button in the list is a tap until the finger has clearly moved down
    drag.current = { y: event.clientY, t: event.timeStamp, id: event.pointerId, live: !fromBody };
  };

  const onPointerMove = (event) => {
    const state = drag.current;
    if (!state || state.id !== event.pointerId) return;
    const dy = event.clientY - state.y;
    if (!state.live) {
      if (dy < 10) {
        if (dy < -6) drag.current = null; // scrolling the list up
        return;
      }
      state.live = true;
      panel.current?.setPointerCapture?.(event.pointerId);
    }
    setPull(Math.max(0, dy));
  };

  const onPointerUp = (event) => {
    const state = drag.current;
    drag.current = null;
    if (!state || !state.live) return;
    const dy = event.clientY - state.y;
    const velocity = dy / Math.max(1, event.timeStamp - state.t);
    if (dy > DISMISS_PX || velocity > DISMISS_VELOCITY) onClose();
    else setPull(0);
  };

  return (
    <div className="sheet-scrim" onClick={onClose} role="presentation">
      <div
        ref={panel}
        className={`sheet ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={pull ? { transform: `translateY(${pull}px)`, transition: 'none' } : undefined}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="sheet-handle" aria-hidden="true" />
        {title && (
          <div className="sheet-head">
            <span className="sheet-title">{title}</span>
            <button type="button" className="sheet-done" onClick={onClose}>
              Done
            </button>
          </div>
        )}
        <div ref={body} className="sheet-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Sheet;
